/**
 * 日のメモ（#299、docs/102 §5.10）。{task}の日・日付の見出しに短いメモ（「資材搬入 10時」）。メモは日付に付く＝{task}を動かしても動かない。
 *   ・DayNotePop：吹き出し（日付「11/14（土）」・文の欄 100字・「保存」・「削除」）。右クリックの「この日のメモ」・点線を押す・見出しの長押しから
 *   ・DayNotesDialog：{task}の ⋮「日ごとのメモ」（開始〜終わりの日ごとに文の欄。範囲の外のメモも下に出す＝消さない）。タブレット・スマホ・キーボード
 *   ・DayNoteMarks：行のセルの下辺の朱の点線（日の表示）・旬・月の表示は朱の小さな点。乗せると吹き出し（title）、押すと編集
 * 紙には出さない（CSV の「日のメモ」と協力会社のページには出す）
 */
import { SCHEDULE_LIMITS, addDays, mdwJaOf, type ScheduleDayNote, type ScheduleRow, type WorkCalendar } from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./msg.js";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { guardComposingEnter } from "../../components/ui.jsx";
import { Dlg } from "./Dialogs.jsx";
import type { Axis } from "./Gantt.jsx";

/** 吹き出しの中身（どこで開いたか） */
export type DayNoteTarget = { rowId: string | null; date: string; at: { x: number; y: number }; name?: string };

/* ---------------- 吹き出し ---------------- */
export function DayNotePop({
  target,
  holidays,
  text,
  onSave,
  onClose,
}: {
  target: DayNoteTarget;
  holidays: ReadonlySet<string>;
  /** 今のメモ（無ければ ""） */
  text: string;
  /** 文を保存（空なら削除） */
  onSave: (text: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(text);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useLayoutEffect(() => {
    const w = ref.current?.offsetWidth ?? 320;
    const h = ref.current?.offsetHeight ?? 140;
    const { x, y } = target.at;
    const top = y + 8 + h <= window.innerHeight - 8 ? y + 8 : Math.max(8, y - 8 - h);
    setPos({ top, left: Math.max(8, Math.min(x - 24, window.innerWidth - w - 8)) });
  }, [target.at]);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey, true);
    };
  }, []);
  const mdw = mdwJaOf(target.date, holidays);
  const save = () => {
    const t = value.trim();
    if (t === text.trim()) {
      onClose();
      return;
    }
    onSave(t);
  };
  return createPortal(
    <div ref={ref} className="sch-notepop" role="dialog" aria-label={`${mdw} ${SCHEDULE_MSG.dayNoteText}`} style={pos ?? { top: -9999, left: -9999 }} data-testid="sch-notepop">
      <div className="hd">
        <b data-testid="sch-notepop-date">{mdw}</b>
        <span className="muted">{target.rowId === null ? SCHEDULE_MSG.dayNoteSiteLabel : target.name || ""}</span>
      </div>
      <input
        ref={input}
        className="inp"
        type="text"
        value={value}
        maxLength={SCHEDULE_LIMITS.dayNoteText}
        aria-label={SCHEDULE_MSG.dayNoteText}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          guardComposingEnter(e);
          if (e.key === "Enter" && !e.defaultPrevented) {
            e.preventDefault();
            save();
          }
        }}
        data-testid="sch-notepop-text"
      />
      <div className="ft">
        <small className="muted">
          {value.length}／{SCHEDULE_LIMITS.dayNoteText}
        </small>
        <span className="sp" />
        {text ? (
          <button type="button" className="btn text danger" onClick={() => onSave("")} data-testid="sch-notepop-delete">
            {SCHEDULE_MSG.dayNoteDelete}
          </button>
        ) : null}
        <button type="button" className="btn primary" onClick={save} data-testid="sch-notepop-save">
          {SCHEDULE_MSG.dayNoteSave}
        </button>
      </div>
    </div>,
    document.body,
  );
}

/* ---------------- ⋮「日ごとのメモ」 ---------------- */
/** 1日の欄（打ち終わり＝欄を出る・Enter で保存。空にすると削除） */
function NoteLine({ day, label, text, onSave }: { day: string; label: string; text: string; onSave: (day: string, text: string) => void }) {
  const [v, setV] = useState(text);
  useEffect(() => setV(text), [text]);
  const commit = () => {
    if (v.trim() !== text.trim()) onSave(day, v);
  };
  return (
    <label className="sch-dnline" data-testid="sch-daynote-line" data-date={day}>
      <span className="d">{label}</span>
      <input
        className="inp"
        type="text"
        value={v}
        maxLength={SCHEDULE_LIMITS.dayNoteText}
        aria-label={`${label} ${SCHEDULE_MSG.dayNoteText}`}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          guardComposingEnter(e);
          if (e.key === "Enter" && !e.defaultPrevented) {
            e.preventDefault();
            commit();
          }
        }}
        data-testid="sch-daynote-text"
      />
    </label>
  );
}

export function DayNotesDialog({
  row,
  notes,
  holidays,
  onSave,
  onClose,
}: {
  row: ScheduleRow;
  notes: readonly ScheduleDayNote[];
  holidays: ReadonlySet<string>;
  onSave: (day: string, text: string) => void;
  onClose: () => void;
}) {
  const own = useMemo(() => new Map(notes.filter((x) => x.rowId === row.id).map((x) => [x.day, x.text])), [notes, row.id]);
  const start = row.start;
  const end = row.end ?? row.start;
  // 開始〜終わりの日（長い{task}でも 400日まで）。範囲の外のメモは「期間外」に（消さない）
  const days = useMemo(() => {
    if (!start || !end) return [];
    const out: string[] = [];
    for (let d = start; d <= end && out.length < 400; d = addDays(d, 1)) out.push(d);
    return out;
  }, [start, end]);
  const outside = [...own.keys()].filter((d) => !start || !end || d < start || d > end).sort();
  return (
    <Dlg
      title={SCHEDULE_MSG.dayNotesTitle}
      testId="sch-daynotes"
      onCancel={onClose}
      foot={
        <button type="button" className="btn primary" onClick={onClose} data-testid="sch-daynotes-close">
          {SCHEDULE_MSG.close}
        </button>
      }
    >
      <p className="sch-rdname">
        <b>{row.name || "（名前なし）"}</b>
      </p>
      <div className="sch-dnlist">
        {days.map((d) => (
          <NoteLine key={d} day={d} label={mdwJaOf(d, holidays)} text={own.get(d) ?? ""} onSave={onSave} />
        ))}
        {outside.length ? <b className="sch-dnout">{SCHEDULE_MSG.dayNotesOutside}</b> : null}
        {outside.map((d) => (
          <NoteLine key={d} day={d} label={mdwJaOf(d, holidays)} text={own.get(d) ?? ""} onSave={onSave} />
        ))}
      </div>
    </Dlg>
  );
}

/* ---------------- 行の点線・点 ---------------- */
export function DayNoteMarks({
  notes,
  ax,
  cal,
  coarse,
  name,
  rowId,
  readOnly,
  onEdit,
}: {
  /** その行のメモ（日付 → 文） */
  notes: ReadonlyMap<string, string> | undefined;
  ax: Axis;
  cal: WorkCalendar;
  /** 旬・月の表示（朱の小さな点） */
  coarse: boolean;
  name?: string;
  rowId: string | null;
  readOnly: boolean;
  onEdit?: ((t: DayNoteTarget) => void) | undefined;
}) {
  if (!notes?.size) return null;
  return (
    <>
      {[...notes].map(([d, text]) => {
        // 文は利用者の字（言葉の置き換え t() を通さない）
        const tip = `${mdwJaOf(d, cal.holidays)} ${text}`;
        const style = coarse ? { left: ax.x(d) + ax.w(d) / 2 - 3 } : { left: ax.x(d), width: ax.w(d) };
        const cls = "sch-dnote" + (coarse ? " dot" : "");
        return !readOnly && onEdit && !coarse ? (
          <button
            key={d}
            type="button"
            className={cls}
            style={style}
            title={tip}
            aria-label={tip}
            tabIndex={-1}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => onEdit({ rowId, date: d, at: { x: e.clientX, y: e.clientY }, ...(name !== undefined ? { name } : {}) })}
            data-testid="sch-dnote"
            data-date={d}
          />
        ) : (
          <span key={d} className={cls} style={style} title={tip} data-testid="sch-dnote" data-date={d} />
        );
      })}
    </>
  );
}
